/**
 * Account-state + membership badge color pairs (DNKADM-0269) — moved here from
 * `src/features/members/accountState.ts` so the pairs are mode-aware and the
 * feature dir stays literal-free (the DNKADM-0269 ESLint guard bans raw colors
 * in `src/features/**`; `src/theme/**` owns them).
 *
 * LIGHT values are byte-identical to the pre-0269 literals (the 0266 identity
 * constraint): Unclaimed reuses the amber-neutral GUEST-chip pair, Disabled the
 * `STATUS.cancelled` pair, Invited the teal ADMIN-chip pair (DNKADM-0181 WCAG
 * remediation). Dark values follow the member app's translucent-tint +
 * lifted-ink ramp (same families as `STATUS_DARK` in `../tokens.ts`).
 *
 * The precedence/derivation LOGIC stays in `accountState.ts` — this module is
 * data only.
 */

/** One badge ink/tint pair. */
interface BadgePair {
  fg: string;
  bg: string;
}

export interface AccountBadgePairs {
  /** Membership-derived labels (keyed by `membershipState`). */
  membership: { USER: BadgePair; ACTIVE_MEMBER: BadgePair; INACTIVE_MEMBER: BadgePair };
  /** The account-state precedence pairs. */
  unclaimed: BadgePair;
  disabled: BadgePair;
  invited: BadgePair;
  /** Fallback for an unknown future `membershipState` (renders raw). */
  unknown: BadgePair;
}

const LIGHT: AccountBadgePairs = {
  membership: {
    USER: { fg: '#5B5470', bg: '#ECEAF3' },
    ACTIVE_MEMBER: { fg: '#1F6B43', bg: '#E5F1E9' },
    INACTIVE_MEMBER: { fg: '#8A5A12', bg: '#FBEBD3' },
  },
  unclaimed: { fg: '#6A5A2A', bg: '#EFE9D9' },
  disabled: { fg: '#9A4128', bg: '#F4E3DE' },
  invited: { fg: '#256A6E', bg: '#E2EDEF' },
  unknown: { fg: '#5B5470', bg: '#ECEAF3' },
};

const DARK: AccountBadgePairs = {
  membership: {
    USER: { fg: '#B3ABCB', bg: 'rgba(139,130,168,0.2)' },
    ACTIVE_MEMBER: { fg: '#7FD3A3', bg: 'rgba(46,157,94,0.18)' },
    INACTIVE_MEMBER: { fg: '#E8C27A', bg: 'rgba(216,154,46,0.16)' },
  },
  unclaimed: { fg: '#D8C692', bg: 'rgba(154,124,42,0.22)' },
  disabled: { fg: '#F0A090', bg: 'rgba(192,73,46,0.18)' },
  invited: { fg: '#8FD0D4', bg: 'rgba(37,106,110,0.26)' },
  unknown: { fg: '#B3ABCB', bg: 'rgba(139,130,168,0.2)' },
};

/** Per-mode account/membership badge pairs. Light === the pre-0269 literals. */
export function buildAccountBadgePairs(mode: 'light' | 'dark'): AccountBadgePairs {
  return mode === 'dark' ? DARK : LIGHT;
}
